import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': `${SITE_URL}/about#webpage`,
      url: `${SITE_URL}/about`,
      name: '会社概要｜岡山市の工務店・リフォーム専門｜株式会社おかやま工務店',
      description:
        '株式会社おかやま工務店の会社概要。代表取締役・岡山太郎が率いる岡山市北区の工務店。設立2016年、建設業許可取得。外装・内装リフォーム40年の実績。',
      inLanguage: 'ja',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#business` },
      dateModified: new Date().toISOString().split('T')[0],
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', 'h2'],
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ホーム', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: '会社概要', item: `${SITE_URL}/about` },
        ],
      },
    };
    const orgSchema = {
      '@context': 'https://schema.org',
      '@type': 'HomeAndConstructionBusiness',
      '@id': `${SITE_URL}/#business`,
      name: '株式会社おかやま工務店',
      url: `${SITE_URL}/`,
      telephone: '086-012-3456',
      faxNumber: '086-012-3457',
      foundingDate: '2016',
      numberOfEmployees: { '@type': 'QuantitativeValue', value: '10' },
      founder: {
        '@type': 'Person',
        name: '岡山太郎',
        jobTitle: '代表取締役',
        worksFor: { '@id': `${SITE_URL}/#business` },
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '○○町1-2-3',
        addressLocality: '岡山市北区',
        addressRegion: '岡山県',
        postalCode: '700-0000',
        addressCountry: 'JP',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 34.6617,
        longitude: 133.9350,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '08:00',
          closes: '17:00',
        },
      ],
      areaServed: [
        { '@type': 'City', name: '岡山市' },
        { '@type': 'City', name: '倉敷市' },
        { '@type': 'City', name: '総社市' },
        { '@type': 'City', name: '玉野市' },
        { '@type': 'City', name: '赤磐市' },
      ],
      hasCredential: [
        { '@type': 'EducationalOccupationalCredential', name: '建設業許可（岡山県知事許可）' },
        { '@type': 'EducationalOccupationalCredential', name: '一級建築士事務所登録' },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-about';
    script.textContent = JSON.stringify([schema, orgSchema]);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-about');
      if (el) el.remove();
    };
  }, []);

  const companyInfo = [
    { label: '会社名', value: '株式会社おかやま工務店' },
    { label: '代表取締役', value: '岡山太郎' },
    { label: '所在地', value: '〒700-0000 岡山市北区○○町1-2-3' },
    { label: '電話番号', value: '086-012-3456' },
    { label: 'FAX', value: '086-012-3457' },
    { label: '設立', value: '2016年' },
    { label: '営業時間', value: '8:00 〜 17:00' },
    { label: '定休日', value: '日曜日・祝日' },
    { label: '許可・資格', value: '建設業許可（般-○○）第○○○○○号\n一級建築士事務所登録\n岡山県知事許可' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/31dfa784442463861673401a1bec496e.jpg"
            alt="会社概要"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20"></div>
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 w-full">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              会社概要
            </h1>
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section className="py-16 lg:py-24 px-6 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left Column: Company Info Table */}
            <div>
              <h2 className="text-2xl lg:text-3xl font-black text-[#1a1a2e] mb-8" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                会社情報
              </h2>
              <div className="space-y-6">
                {companyInfo.map((info, index) => (
                  <div key={index} className="flex flex-col sm:flex-row border-b border-gray-200 pb-4">
                    <dt className="w-full sm:w-1/3 text-sm font-bold text-gray-600 mb-2 sm:mb-0">
                      {info.label}
                    </dt>
                    <dd className="w-full sm:w-2/3 text-base text-[#1a1a2e] whitespace-pre-line">
                      {info.value}
                    </dd>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column: President Message */}
            <div>
              <h2 className="text-2xl lg:text-3xl font-black text-[#1a1a2e] mb-8" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                代表挨拶
              </h2>
              <div className="mb-8">
                <div className="w-48 h-48 mx-auto rounded-full overflow-hidden shadow-xl mb-6">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20portrait%20of%20Japanese%20male%20company%20president%20in%20his%2060s%20wearing%20business%20casual%20attire%20confident%20friendly%20smile%20trustworthy%20expression%20natural%20lighting%20simple%20clean%20neutral%20background%20professional%20headshot%20experienced%20leader&width=400&height=400&seq=president&orientation=squarish"
                    alt="代表取締役 岡山太郎"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-center text-lg font-bold text-[#1a1a2e] mb-2">
                  代表取締役
                </p>
                <p className="text-center text-2xl font-black text-[#1a1a2e]" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  岡山 太郎
                </p>
              </div>

              <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                <p>
                  株式会社おかやま工務店のホームページをご覧いただき、誠にありがとうございます。
                </p>
                <p>
                  私は大工として40年以上、岡山市を中心に住宅の修理や内装・外装工事に携わってまいりました。長年の経験を通じて学んだことは、お客様一人ひとりの住まいへの想いを大切にし、確かな技術で応えることの重要性です。
                </p>
                <p>
                  どんな小さな修理でも、お客様にとっては大切な住まいの一部です。私たちは、お客様の立場に立ち、丁寧な説明と誠実な対応を心がけております。
                </p>
                <p>
                  これからも地域に根ざした工務店として、お客様の快適な暮らしを支えてまいります。住まいのことで何かお困りのことがございましたら、どうぞお気軽にご相談ください。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 lg:py-24 px-6 bg-[#faf8f3]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-2xl lg:text-3xl font-black text-[#1a1a2e] mb-8 text-center" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            アクセス
          </h2>

          <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
            <div className="aspect-video w-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52634.64842229488!2d133.89234!3d34.66167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x355402e2a907e6e1%3A0x53ab4e8e7d00e31e!2z5bKh5bGx5biC5YyX5Yy6!5e0!3m2!1sja!2sjp!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="株式会社おかやま工務店の地図"
              ></iframe>
            </div>
            <div className="p-8">
              <h3 className="text-xl font-bold text-[#1a1a2e] mb-4">
                株式会社おかやま工務店
              </h3>
              <div className="space-y-2 text-gray-700">
                <p className="flex items-start">
                  <i className="ri-map-pin-line text-[#ff6b35] text-xl mr-3 mt-1 flex-shrink-0"></i>
                  <span>〒700-0000 岡山市北区○○町1-2-3</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-phone-line text-[#ff6b35] text-xl mr-3 mt-1 flex-shrink-0"></i>
                  <span>TEL: 086-012-3456 / FAX: 086-012-3457</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-time-line text-[#ff6b35] text-xl mr-3 mt-1 flex-shrink-0"></i>
                  <span>営業時間: 8:00 〜 17:00（定休日: 日曜日・祝日）</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a1a2e] mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            お気軽にご相談ください
          </h2>
          <p className="text-lg text-gray-700 mb-10 leading-relaxed">
            見積もり無料・即日対応可能<br />
            経験豊富なスタッフが丁寧に対応いたします
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:086-012-3456"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#ff6b35] text-white text-base lg:text-lg font-bold rounded-full hover:bg-[#ff8555] transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill mr-2"></i>
              086-012-3456
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#1a1a2e] text-white text-base lg:text-lg font-bold rounded-full hover:bg-[#2a2a3e] transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-mail-line mr-2"></i>
              お問い合わせフォーム
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;